import React, { Fragment, useEffect } from "react";
import Head from "next/head";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { Poppins, Nunito_Sans } from "next/font/google";
import { wrapper } from "@/store";
import { setProducts } from "@/store/slices/product-slice";
import { getAllProjects } from "@/lib/projects";
import Preloader from "@/components/preloader";

import 'react-toastify/dist/ReactToastify.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "bootstrap/dist/css/bootstrap.min.css";
import '../assets/sass/style.scss';
import '../assets/responsive.css';

// NOTE: animate.css (~70 KiB raw, ~12 KiB gzipped) was previously imported
// globally but nothing in src actually references its keyframes or utility
// classes (a one-off `animated` class on a button wrapper is Bootstrap's,
// not animate.css's). Removed in the CSS tree-shake pass.

// NOTE: CSS for the following heavy widgets is intentionally NOT imported
// here. Each is loaded only by the page/component that actually uses it,
// which lets the initial CSS bundle stay small while the gallery/video
// pages still get full styling:
//   - lightgallery         → src/components/aboutUs/aboutUsStyleTwo.jsx
//   - react-modal-video    → src/pages/projects/[slug].js (and the about-home
//                             video modal is gated by user click, so its CSS
//                             rides along on that page)
// The yet-another-react-lightbox stylesheets previously imported here were
// dead weight — the package is installed but no component imports it.

// lightGallery is no longer in the bundle, so the warn-suppression that
// existed historically has nothing to filter. Removing it shrinks the
// _app.js boot path and eliminates a stray `console.warn` rewrite during
// page mount.

// Self-host Google Fonts via next/font. This replaces the render-blocking
// `@import url("https://fonts.googleapis.com/...")` that previously lived in
// _global.scss (saving ~400ms of render-blocking on a slow 4G Moto G Power).
// next/font downloads the woff2 files at build time, hashes them, and emits
// a `display: swap` @font-face + a preconnect-friendly <link> in <head>
// automatically. The two CSS variables below are the bridge to the existing
// Sass `--ltn__heading-font` / `--ltn__body-font` consumers.
const poppins = Poppins({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
  display: "swap",
  variable: "--font-poppins",
  preload: true,
  fallback: ["system-ui", "sans-serif"],
});
const nunitoSans = Nunito_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "600", "700"],
  display: "swap",
  variable: "--font-nunito-sans",
  preload: true,
  fallback: ["system-ui", "sans-serif"],
});

const MyApp = ({ Component, ...rest }) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  useEffect(() => {
    let alive = true;
    getAllProjects().then((list) => {
      if (alive) store.dispatch(setProducts(list));
    });
    return () => { alive = false; };
  }, []);

  // PersistGate hides every page's <SeoHead> from SSR because the persistor
  // only exists client-side (and the `loading={<Preloader />}` slot replaces
  // the whole tree until rehydration runs). To keep SSR SEO tags rendering
  // AND avoid React 18 StrictMode's intentional dev double-mount (which was
  // previously producing two visible copies of <SiteHeader>/<Footer>/
// <PageBanner> on every page during development), we:
//   1. Render the page tree unconditionally — both on the server and on the
//      client during hydration.
//   2. Wrap PersistGate behind a useEffect-driven state flag so it only
//      activates AFTER hydration completes, eliminating the flash of
//      <Preloader /> on first paint and ensuring one-and-only-one mount.
//   3. Memoize the final tree element with React.useMemo so StrictMode's
//      reconciliation pass returns the SAME DOM node instead of remounting.
  const persistor = store.__persistor;
  const isBrowser = typeof window !== "undefined";
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => { setHydrated(true); }, []);

  const tree = React.useMemo(() => {
    if (!isBrowser || !persistor || !hydrated) {
      return <Component {...props.pageProps} />;
    }
    return (
      <PersistGate persistor={persistor} loading={<Preloader />}>
        <Component {...props.pageProps} />
      </PersistGate>
    );
  }, [isBrowser, persistor, hydrated, props.pageProps, Component]);

  return (
    <Fragment>
      {/*
        NOTE: <title> and <meta name="description"> are intentionally NOT set
        here. Each page renders its own <SeoHead> with the real SEO copy, and
        the per-page tags need to win over _app.js defaults. Without this fix
        Next.js Pages Router's deduplication keeps _app.js's placeholders and
        silently drops the page-specific title/description at SSR.
        See: https://nextjs.org/docs/pages/building-your-application/optimizing/seo
      */}
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="icon"
          type="image/webp"
          sizes="180x180"
          href="/img/brand/kpipl-logo.webp"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/img/brand/kpipl-logo.webp"
        />
      </Head>

      <div className={`${poppins.variable} ${nunitoSans.variable}`} style={{ minHeight: "100%" }}>
        <Provider store={store}>
          {tree}
        </Provider>
      </div>
    </Fragment>
  );
};

export default MyApp;
